import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BackButton from '../components/BackButton';
import ProgressBar from '../components/ProgressBar';
import { getServices } from '../services/enhancedApi';
import { Service } from '../services/types';
import { useAppState } from '../state/AppState';

/**
 * ScreenG - Service selection screen.
 *
 * Allows the user to select the service they are looking for.
 */
const ScreenG: React.FC = () => {
  const navigate = useNavigate();
  const { dispatch, state } = useAppState();
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadServices = async () => {
      try {
        const data = await getServices(state.availabilities);
        // Sort services with specific order: Individual, Couples, Family, Group, then others alphabetically
        const serviceOrder = [
          'Individual Therapy',
          'Couples Therapy',
          'Family Therapy',
          'Group Therapy'
        ];
        
        const sortedData = data.sort((a, b) => {
          const indexA = serviceOrder.indexOf(a.name);
          const indexB = serviceOrder.indexOf(b.name);
          
          // If both are in the ordered list, sort by their position
          if (indexA !== -1 && indexB !== -1) {
            return indexA - indexB;
          }
          // If only A is in the ordered list, A comes first
          if (indexA !== -1) {
            return -1;
          }
          // If only B is in the ordered list, B comes first
          if (indexB !== -1) {
            return 1;
          }
          // If neither is in the ordered list, sort alphabetically
          return a.name.localeCompare(b.name);
        });
        
        setServices(sortedData);
      } catch (error) {
        setServices([]);
      } finally {
        setLoading(false);
      }
    };

    // Add a small delay to ensure availability data is loaded
    const timeoutId = setTimeout(loadServices, 500);
    
    return () => clearTimeout(timeoutId);
  }, [state.availabilities]);

  const handleService = (serviceName: string) => {
    dispatch({ type: 'SET_SERVICE', payload: serviceName });
    setTimeout(() => navigate('/h'), 150); // slight delay for button feedback
  };

  const handleOptionNotListed = () => {
    navigate('/v');
  };

  if (loading) {
    return (
      <div className="screen-bg">
        <div className="main-content-container">
          <div className="mtg-back-btn-align">
            <BackButton onClick={() => navigate(-1)} />
          </div>
          <main className="mtg-screen-main" id="main-content">
            <div style={{ textAlign: 'center', padding: '48px 0' }}>
              <div className="loading-spinner" style={{ margin: '0 auto 16px auto' }} />
              <div style={{ color: '#b5b5b5', fontWeight: 500 }} role="status" aria-live="polite">Loading...</div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="screen-bg">
      <div className="main-content-container">
        <div className="mtg-back-btn-align">
          <BackButton onClick={() => navigate(-1)} />
        </div>
        <main className="mtg-screen-main" id="main-content">
          <div className="mtg-screen-card mb-18">
            <h2 className="mtg-screen-question mb-24">
              What service are you looking for?
            </h2>
            <div role="listbox" aria-label="Service selection" className="button-container">
              {services.map((service) => (
                <button
                  key={service.id}
                  className="pill-btn-standard mb-24"
                  onClick={() => handleService(service.name)}
                  role="option"
                  aria-selected="false"
                  aria-label={`Select ${service.name} as your service`}
                >
                  {service.name}
                </button>
              ))}
            </div>
            <ProgressBar progress={18}/>
          </div>
          <button
            className="mtg-secondary-action-btn mb-18"
            onClick={handleOptionNotListed}
            aria-label="My option is not listed, contact us"
          >
            MY OPTION IS NOT LISTED
          </button>
        </main>
      </div>
    </div>
  );
};

export default ScreenG;